import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Users, XCircle } from "lucide-react";
import { apiGet } from "../../Api/Client.js";
import "../../Styles/LmsAnalyticsScreen.css";
import { fmtCurrency, fmtNum, pct, stageColor } from "./crmFormat.js";

// Split 2026-10-07 from the old combined "Analytics & Perf" screen — this half
// is the BA Performance tab (and the SDR/AE funnel beneath it), now its own
// destination under the "Performance & Goals" nav section alongside Growth
// Roadmap. See the matching split in Backend/EnterpriseCore/pages.py.

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, color = "var(--blue-500)", trend }) {
  return (
    <div className="LmsA-Stat-Card">
      <div className="LmsA-Stat-Icon" style={{ background: `${color}18`, color }}>
        {Icon && <Icon size={20} />}
      </div>
      <div className="LmsA-Stat-Body">
        <div className="LmsA-Stat-Value" style={{ color }}>{value}</div>
        <div className="LmsA-Stat-Label">{label}</div>
        {sub && <div className="LmsA-Stat-Sub">{sub}</div>}
      </div>
      {trend != null && (
        <div className={`LmsA-Stat-Trend ${trend >= 0 ? "up" : "down"}`}>
          {trend >= 0 ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
}

// ─── BA Performance ─────────────────────────────────────────────────────────────

function BaPerformanceSection({ data, navigate, company }) {
  const [sort, setSort] = useState("total_leads");
  const [dir, setDir] = useState("desc");
  const [expandedBa, setExpandedBa] = useState(null);

  // SDR / AE funnel — its own window (1d / 7d / 30d) and its own fetch, so it can
  // reload on window change without touching the snapshot table above it.
  const [funnelDays, setFunnelDays] = useState(7);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelError, setFunnelError] = useState(false);
  const [funnelTry, setFunnelTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setFunnelLoading(true);
    setFunnelError(false);
    apiGet(`/Lms/api/sdr-ae-funnel/?days=${funnelDays}`)
      .then((r) => { if (!cancelled) setFunnel(r); })
      // A failed load is not "no movement": it is shown as a failure with a retry.
      .catch(() => { if (!cancelled) { setFunnel(null); setFunnelError(true); } })
      .finally(() => { if (!cancelled) setFunnelLoading(false); });
    return () => { cancelled = true; };
  }, [funnelDays, funnelTry]);

  if (!data) return <div className="LmsA-Loading">Loading BA Performance…</div>;

  const rows = [...(data.rows || [])].sort((a, b) => {
    const av = a[sort] ?? 0;
    const bv = b[sort] ?? 0;
    if (typeof av === "string") return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return dir === "asc" ? av - bv : bv - av;
  });

  const toggleSort = (field) => {
    if (sort === field) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(field); setDir("desc"); }
  };

  const SortIcon = ({ field }) => (
    <span style={{ opacity: sort === field ? 1 : 0.3, marginLeft: 4, fontSize: 11 }}>
      {sort === field && dir === "asc" ? "↑" : "↓"}
    </span>
  );

  return (
    <div className="LmsA-Section">
      <div className="LmsA-Section-Header">
        <div className="LmsA-Section-Title"><Users size={18} /> BA Performance Dashboard</div>
        <div style={{ fontSize: 12, color: "var(--c-500)" }}>
          {data.active_today_count || 0} / {data.total_bas || 0} BAs Active Today
        </div>
      </div>

      {/* Summary cards */}
      <div className="LmsA-Stat-Row">
        <StatCard label="Total BAs" value={data.total_bas || 0} icon={Users} color="var(--indigo-500)" />
        <StatCard label="Active Today" value={data.active_today_count || 0} icon={CheckCircle} color="var(--green-600)"
          sub={`${pct(data.active_today_count, data.total_bas)} Updated Leads`} />
        <StatCard label="Inactive Today" value={(data.inactive_today || []).length} icon={AlertTriangle} color="var(--amber-500)"
          sub="BAs With Leads But No Note Today" />
      </div>

      {/* Alert: BAs not active today */}
      {(data.inactive_today || []).length > 0 && (
        <div className="LmsA-Alert-Strip">
          <AlertTriangle size={16} />
          <span><strong>{(data.inactive_today || []).length} BA{(data.inactive_today || []).length > 1 ? "s" : ""} Have Not Logged Any Activity Today:</strong>{" "}
            {(data.inactive_today || []).map((r) => r.name).join(", ")}
          </span>
        </div>
      )}

      {/* BA Table */}
      <div className="LmsA-Table-Wrap">
        <table className="LmsA-Table">
          <thead>
            <tr>
              <th><button onClick={() => toggleSort("name")}>BA Name <SortIcon field="name" /></button></th>
              <th><button onClick={() => toggleSort("total_leads")}>Total Leads <SortIcon field="total_leads" /></button></th>
              <th><button onClick={() => toggleSort("won_count")}>Won <SortIcon field="won_count" /></button></th>
              <th><button onClick={() => toggleSort("lost_count")}>Lost <SortIcon field="lost_count" /></button></th>
              <th><button onClick={() => toggleSort("conversion_rate")}>Conv. Rate <SortIcon field="conversion_rate" /></button></th>
              <th><button onClick={() => toggleSort("overdue_count")}>Overdue <SortIcon field="overdue_count" /></button></th>
              <th><button onClick={() => toggleSort("notes_today")}>Notes Today <SortIcon field="notes_today" /></button></th>
              <th><button onClick={() => toggleSort("notes_30d")}>Notes (30d) <SortIcon field="notes_30d" /></button></th>
              <th><button onClick={() => toggleSort("value_won")}>Value Won <SortIcon field="value_won" /></button></th>
              <th>Status</th>
              <th>Stages</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: "center", color: "var(--c-400)", padding: "24px" }}>No BA Data Found.</td></tr>
            )}
            {rows.map((ba) => (
              <React.Fragment key={ba.employee_id}>
                <tr className={ba.updated_today ? "" : "LmsA-Row-Inactive"}>
                  <td>
                    <button className="LmsA-Name-Btn" onClick={() => setExpandedBa(expandedBa === ba.employee_id ? null : ba.employee_id)}>
                      <span className="LmsA-BA-Avatar">{String(ba.name || "?")[0].toUpperCase()}</span>
                      <span>{ba.name}</span>
                      {expandedBa === ba.employee_id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    {ba.department && <div style={{ fontSize: 11, color: "var(--c-400)", paddingLeft: 36 }}>{ba.department}</div>}
                  </td>
                  <td><strong>{fmtNum(ba.total_leads)}</strong></td>
                  <td><span className="LmsA-Won-Badge">{ba.won_count}</span></td>
                  <td><span className="LmsA-Lost-Badge">{ba.lost_count}</span></td>
                  <td>
                    <div className="LmsA-Conv-Bar">
                      <div className="LmsA-Conv-Fill" style={{ width: `${Math.min(100, ba.conversion_rate)}%` }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: ba.conversion_rate >= 20 ? "var(--green-600)" : ba.conversion_rate >= 10 ? "var(--amber-600)" : "var(--red-500)" }}>
                      {ba.conversion_rate}%
                    </span>
                  </td>
                  <td>
                    {ba.overdue_count > 0
                      ? <span style={{ color: "var(--red-600)", fontWeight: 700 }}>{ba.overdue_count} ⚠</span>
                      : <span style={{ color: "var(--c-400)" }}>0</span>}
                  </td>
                  <td>
                    {ba.notes_today > 0
                      ? <span className="LmsA-Notes-Today-Badge">{ba.notes_today}</span>
                      : <span className="LmsA-Notes-None-Badge">None</span>}
                  </td>
                  <td>{fmtNum(ba.notes_30d)}</td>
                  <td>{fmtCurrency(ba.value_won, company)}</td>
                  <td>
                    {ba.updated_today
                      ? <span className="LmsA-Status-Active"><CheckCircle size={13} /> Active</span>
                      : <span className="LmsA-Status-Inactive"><XCircle size={13} /> Idle</span>}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(ba.by_stage || []).slice(0, 4).map((s) => (
                        <span key={s.stage} className="LmsA-Stage-Mini" style={{ background: `${stageColor(s.stage)}18`, color: stageColor(s.stage) }}>
                          {s.stage.replace("Closed - ", "").replace("Closed", "").slice(0, 8)} {s.count}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
                {expandedBa === ba.employee_id && (
                  <tr className="LmsA-Expanded-Row">
                    <td colSpan={11}>
                      <div className="LmsA-Expanded-Body">
                        <strong style={{ fontSize: 13, color: "var(--c-900)" }}>Stage Breakdown For {ba.name}</strong>
                        <div className="LmsA-Stage-Breakdown-Grid">
                          {(ba.by_stage || []).map((s) => (
                            <div key={s.stage} className="LmsA-Stage-Item">
                              <div className="LmsA-Stage-Dot" style={{ background: stageColor(s.stage) }} />
                              <span className="LmsA-Stage-Name">{s.stage}</span>
                              <span className="LmsA-Stage-Count">{s.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* SDR / AE Funnel — movement through the pipeline in the chosen window.
          Flow-based (stage moves that happened in the window), split at Demo Done.
          Two blocks so SDR-work and AE-work read separately at a glance; a person
          shows in both, and where their numbers land shows what they're doing. */}
      <div className="LmsA-Sub-Section" style={{ marginTop: 22 }}>
        <div className="LmsA-Sub-Title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>SDR / AE Funnel · movement in the window</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {[1, 7, 30].map((d) => (
              <button key={d} className={`LmsA-Day-Pill ${funnelDays === d ? "active" : ""}`} style={{ fontSize: 11 }} onClick={() => setFunnelDays(d)}>{d}d</button>
            ))}
          </div>
        </div>
        <div className="LmsA-Table-Wrap">
          <table className="LmsA-Table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ verticalAlign: "bottom" }}>Person</th>
                <th rowSpan={2} style={{ textAlign: "right", verticalAlign: "bottom" }}>Touchpoints</th>
                <th colSpan={5} style={{ textAlign: "center", borderLeft: "2px solid var(--c-200)", color: "var(--blue-600)" }}>SDR — get to demo</th>
                <th colSpan={4} style={{ textAlign: "center", borderLeft: "2px solid var(--c-200)", color: "var(--indigo-500)" }}>AE — close it</th>
                <th rowSpan={2} style={{ textAlign: "right", verticalAlign: "bottom", borderLeft: "2px solid var(--c-200)" }}>Lost / Cold</th>
              </tr>
              <tr>
                <th style={{ textAlign: "right", borderLeft: "2px solid var(--c-200)" }}>Contact Attempted</th>
                <th style={{ textAlign: "right" }}>Engaged</th>
                <th style={{ textAlign: "right" }}>Scheduled</th>
                <th style={{ textAlign: "right" }}>Done</th>
                <th style={{ textAlign: "right" }}>SDR %</th>
                <th style={{ textAlign: "right", borderLeft: "2px solid var(--c-200)" }}>Done</th>
                <th style={{ textAlign: "right" }}>Proposal</th>
                <th style={{ textAlign: "right" }}>Won</th>
                <th style={{ textAlign: "right" }}>AE %</th>
              </tr>
            </thead>
            <tbody>
              {funnelLoading && (
                <tr><td colSpan={12} style={{ textAlign: "center", color: "var(--c-400)", padding: 16 }}>Loading…</td></tr>
              )}
              {!funnelLoading && funnelError && (
                <tr><td colSpan={12} style={{ textAlign: "center", color: "var(--red-600)", padding: 16 }}>
                  The pipeline movement could not be loaded.{" "}
                  <button type="button" className="LmsA-Day-Pill" onClick={() => setFunnelTry((n) => n + 1)}>Try again</button>
                </td></tr>
              )}
              {!funnelLoading && !funnelError && (funnel?.rows || []).length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: "center", color: "var(--c-400)", padding: 16 }}>No pipeline movement in this window.</td></tr>
              )}
              {!funnelLoading && !funnelError && (funnel?.rows || []).map((r) => {
                const s = r.sdr, a = r.ae;
                const num = (v) => (v ? { color: "var(--c-900)", fontWeight: 600 } : { color: "var(--c-300)" });
                const pctStyle = (v) => (v == null ? { color: "var(--c-300)" } : { fontWeight: 800, color: v >= 20 ? "var(--green-600)" : v >= 10 ? "var(--amber-600)" : "var(--red-500)" });
                const R = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
                const div = { borderLeft: "2px solid var(--c-200)" };
                return (
                  <tr key={r.employee_id}>
                    <td><strong>{r.name}</strong></td>
                    <td style={{ ...R, ...num(r.touchpoints) }}>{r.touchpoints || "–"}</td>
                    <td style={{ ...R, ...div, ...num(s.contacted) }}>{s.contacted || "–"}</td>
                    <td style={{ ...R, ...num(s.engaged) }}>{s.engaged || "–"}</td>
                    <td style={{ ...R, ...num(s.scheduled) }}>{s.scheduled || "–"}</td>
                    <td style={{ ...R, ...num(s.done) }}>{s.done || "–"}</td>
                    <td style={{ ...R, ...pctStyle(s.pct) }}>{s.pct == null ? "–" : `${s.pct}%`}</td>
                    <td style={{ ...R, ...div, ...num(a.done) }}>{a.done || "–"}</td>
                    <td style={{ ...R, ...num(a.proposal) }}>{a.proposal || "–"}</td>
                    <td style={{ ...R, ...num(a.won) }}>{a.won || "–"}</td>
                    <td style={{ ...R, ...pctStyle(a.pct) }}>{a.pct == null ? "–" : `${a.pct}%`}</td>
                    <td style={{ ...R, ...div, color: "var(--c-500)", fontSize: 12.5 }}>{(r.lost || r.cold) ? `${r.lost} / ${r.cold}` : "–"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11.5, color: "var(--c-500)", marginTop: 6, lineHeight: 1.5 }}>
          Counts are stage moves that happened in the window — a person can show in both blocks (Demo Done is the hand-off). SDR % = Done ÷ (Engaged + Scheduled); AE % = Won ÷ Done. A touchpoint is a note carrying a real call/meeting recording link — a plain note doesn't count. <em>Why deals are lost/cold, per person, comes next.</em>
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ───────────────────────────────────────────────────────────────

export function SalesPerformanceScreen({ navigate, data: appData }) {
  const [baData, setBaData] = useState(null);
  const [error, setError] = useState("");
  const [tryAgain, setTryAgain] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError("");
    apiGet("/Lms/api/ba-performance/")
      .then((r) => { if (!cancelled) setBaData(r); })
      .catch(() => { if (!cancelled) setError("Failed to load performance data."); });
    return () => { cancelled = true; };
  }, [tryAgain]);

  return (
    <div className="LmsA-Screen">
      {error && (
        <div className="LmsA-Error">
          <AlertTriangle size={16} /> {error}{" "}
          <button type="button" className="LmsA-Day-Pill" onClick={() => setTryAgain((n) => n + 1)}>Try again</button>
        </div>
      )}
      <div className="LmsA-Content">
        {/* No "Loading..." beside the error: a failed load must not look like one still running. */}
        {!error && <BaPerformanceSection data={baData} navigate={navigate} company={appData?.me?.company} />}
      </div>
    </div>
  );
}
